import { CSSProperties, ReactNode, useEffect } from 'react';
import { getInitialParams } from '@frontend/env';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  DropdownField,
  Text,
  TextLink,
  BaseFieldProps,
  useModalControl,
  SkeletonLoader,
} from '@frontend/design-system';
import { buildAudioLibraryPath } from '../../utils/media-path';
import { getNameWithoutExtension, noMediaID } from '../../utils/phone-utils';
import { useAudioQuery } from '../../utils/use-audio-query';
import { CachedAudioScrubber } from '../common/cached-audio-scrubber';
import { AudioUploadModal } from './audio-upload-modal';
import type { AudioItem } from './types';

export const DEFAULT_GREETING_ID = getInitialParams().backendApi?.includes('api.weavedev.net')
  ? '305a7cd9-21ae-4430-af05-1d2a9a24dc6a'
  : '3144d7d9-e84a-45e1-b94d-a4e209990899';
/** This is the path we will use for default greetings */
export const DEFAULT_GREETING_PATH = getInitialParams().backendApi?.includes('api.weavedev.net')
  ? `https://api.weavedev.net/phone/audio-library/v1/system/download/${DEFAULT_GREETING_ID}`
  : `https://api.weaveconnect.com/phone/audio-library/v1/system/download/${DEFAULT_GREETING_ID}`;

export const CALL_RECORDING_NOTICE_ID = getInitialParams().backendApi?.includes('api.weavedev.net')
  ? '275ffaf7-18a5-4d71-ba0a-4134a08d26e5'
  : '8c52e208-03e3-49c0-9ed6-f9ef33459b5c';
/** This is the path we will use for the Weave Call Recording Notice audio */
export const CALL_RECORDING_NOTICE_PATH = getInitialParams().backendApi?.includes('api.weavedev.net')
  ? `https://api.weavedev.net/phone/audio-library/v1/system/download/${CALL_RECORDING_NOTICE_ID}`
  : `https://api.weaveconnect.com/phone/audio-library/v1/system/download/${CALL_RECORDING_NOTICE_ID}`;

const numberToPx = (num: number | string) => (typeof num === 'number' ? `${num}px` : num);

export type AudioPickerProps = {
  tenantId: string;
  mailboxId?: string;
  field: BaseFieldProps;
  singlePlayer?: boolean;
  hidePlayer?: boolean;
  enableLoader?: boolean;
  allowedOptions?: {
    standard?: boolean;
    custom?: boolean;
    mailbox?: boolean;
    add?: boolean;
    none?: boolean;
    default?: boolean;
    recent?: boolean;
    callRecordingNotice?: boolean;
  };
  lastUsedMediaItemId?: string;
  labels?: {
    field?: string;
    placeholder?: string;
    standard?: string;
    custom?: string;
    mailbox?: string;
    add?: string;
    none?: string;
    default?: string;
    recent?: string;
    callRecordingNotice?: string;
  };
  helperText?: string;
  name: string;
  /**
   * Typically the id of the selected audio item will be available in the field value.
   * However, if that is not sufficient, this callback will provide access to the selected audio item itself.
   * @param item The selected audio item
   */
  onSelect?: (item: AudioItem) => void;
  widths?: {
    field?: CSSProperties['width'];
    scrubber?: CSSProperties['width'];
  };
  className?: string;
  endAdornment?: ReactNode;
  disabled?: boolean;
  hideTTS?: boolean;
};

export const AudioPicker = ({
  tenantId,
  mailboxId,
  field,
  singlePlayer = true,
  hidePlayer = false,
  allowedOptions = {
    standard: false,
    custom: true,
    mailbox: false,
    add: false,
    none: false,
    default: false,
    recent: false,
    callRecordingNotice: false,
  },
  labels,
  helperText,
  name,
  onSelect,
  widths,
  className,
  enableLoader = false,
  endAdornment,
  lastUsedMediaItemId,
  disabled = false,
  hideTTS = false,
}: AudioPickerProps) => {
  const { t } = useTranslation('phone');
  const { modalProps, triggerProps } = useModalControl();
  const { data, isLoading } = useAudioQuery({ tenantId, allowedOptions, mailboxId });

  const getMediaNameById = (id: string) => {
    const media = data?.customMedia.find((item) => item.id === id);
    return media ? getNameWithoutExtension(media.name) : null;
  };

  useEffect(() => {
    if (onSelect && data?.allMedia) {
      const media = findMediaItem(field.value, data.allMedia);
      if (media) {
        onSelect(media);
      }
    }
  }, [field.value, data]);

  if (isLoading && enableLoader) {
    return <SkeletonLoader width={widths?.field || 250} height={40} />;
  }

  if (!data) {
    return null;
  }

  const media = findMediaItem(field.value, data.allMedia);
  const resolvedPath = media ? buildAudioLibraryPath({ media }) : '';
  const resolvedWidths = {
    field: widths?.field ?? 'auto',
    scrubber: widths?.scrubber ?? 'auto',
  };

  return (
    <>
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: widths
            ? `${numberToPx(resolvedWidths.field)} ${numberToPx(resolvedWidths.scrubber)}`
            : 'repeat(auto-fit, minmax(100px, 100%))',
          gap: theme.spacing(2),
        }}
        className={`audio-picker ${className ?? ''}`}
      >
        <DropdownField
          {...field}
          name={name}
          label={labels?.placeholder ?? labels?.field ?? (mailboxId ? t('Greeting') : t('Audio File'))}
          placeholder={labels?.placeholder ?? t('Select one')}
          helperText={helperText}
          endAdornment={endAdornment}
          disabled={disabled}
          css={{ backgroundColor: theme.colors.white }}
        >
          {/* Add Option */}
          {allowedOptions.add && (
            <DropdownField.OptionGroup label=''>
              <div onClick={triggerProps.onClick}>
                <DropdownField.Option value='' isSelectable={false}>
                  <TextLink css={{ cursor: 'pointer' }} weight='bold'>
                    {labels?.add ?? t('Add Audio File')}
                  </TextLink>
                </DropdownField.Option>
              </div>
            </DropdownField.OptionGroup>
          )}

          {/* None Option */}
          {allowedOptions.none && (
            <DropdownField.Option key={'none'} value={name === 'voicemailMediaId' ? noMediaID : 'none'}>
              <Text color='light' weight='bold'>
                {labels?.none ?? t('No Greeting')}
              </Text>
            </DropdownField.Option>
          )}

          {/* Recent Option */}
          {allowedOptions.recent && !!lastUsedMediaItemId && (
            <DropdownField.OptionGroup label={labels?.recent ?? t('Most Recently Used')}>
              <DropdownField.Option
                key={lastUsedMediaItemId}
                value={lastUsedMediaItemId}
                css={{ '&:hover': { backgroundColor: theme.colors.neutral5 } }}
              >
                {getMediaNameById(lastUsedMediaItemId)}
              </DropdownField.Option>
            </DropdownField.OptionGroup>
          )}

          {(allowedOptions.default || allowedOptions.callRecordingNotice) && (
            <DropdownField.OptionGroup label={labels?.default ?? t('Weave Media')}>
              {/* Default Option */}
              {allowedOptions.default && (
                <DropdownField.Option key={'default'} value={'default'}>
                  <Text color='light' weight='bold'>
                    {labels?.default ?? t('Default Greeting')}
                  </Text>
                </DropdownField.Option>
              )}

              {/* Call Recording Notice Option */}
              {allowedOptions.callRecordingNotice && (
                <DropdownField.Option key={'call-recording-notice'} value={CALL_RECORDING_NOTICE_ID}>
                  <Text color='light' weight='bold'>
                    {labels?.callRecordingNotice ?? t('Call Recording Notice')}
                  </Text>
                </DropdownField.Option>
              )}
            </DropdownField.OptionGroup>
          )}

          {/* Standard Options */}
          {allowedOptions.standard && (
            <DropdownField.OptionGroup label={labels?.standard ?? t('Weave Audio')}>
              {data?.standardMedia.map((media) => (
                <DropdownField.Option key={media.id} value={media.id}>
                  {getNameWithoutExtension(media.name)}
                </DropdownField.Option>
              ))}
            </DropdownField.OptionGroup>
          )}
          {/* Voicemail Greeting options */}
          {allowedOptions.mailbox && data.mailboxGreetings.length > 0 && (
            <DropdownField.OptionGroup label={labels?.mailbox ?? t('VM Box Greetings')}>
              {data?.mailboxGreetings.map((media) => (
                <DropdownField.Option key={media.id} value={media.id}>
                  {`${media.number}: ${getNameWithoutExtension(media.name) || t('Unnamed Greeting')}`}
                </DropdownField.Option>
              ))}
            </DropdownField.OptionGroup>
          )}
          {/* Custom Options */}
          {allowedOptions.custom && data?.customMedia.length > 0 && (
            <DropdownField.OptionGroup label={labels?.custom ?? t('Audio Library')}>
              {data?.customMedia.map((media) => (
                <DropdownField.Option key={media.id} value={media.id}>
                  {getNameWithoutExtension(media.name)}
                </DropdownField.Option>
              ))}
            </DropdownField.OptionGroup>
          )}
        </DropdownField>
        {!hidePlayer && resolvedPath && (
          <CachedAudioScrubber
            singlePlayer={singlePlayer}
            css={{
              alignSelf: 'start',
            }}
            mediaId={field.value}
            filePath={resolvedPath}
          />
        )}
      </div>
      <AudioUploadModal
        hideTTS={hideTTS}
        modalProps={modalProps}
        onSuccess={(id) => {
          // @ts-ignore
          field.onChange({ name: field.name, value: id });
        }}
      />
    </>
  );
};

export const findMediaItem = (mediaId: string, data: AudioItem[]) => {
  if (mediaId === 'default') {
    return {
      id: 'default',
      path: DEFAULT_GREETING_PATH,
      isGlobal: true,
      name: 'Default Greeting',
      type: 'standard' as const,
    };
  }

  if (mediaId === CALL_RECORDING_NOTICE_ID) {
    return {
      id: CALL_RECORDING_NOTICE_ID,
      path: CALL_RECORDING_NOTICE_PATH,
      isGlobal: true,
      name: 'Call Recording Notice',
      type: 'standard' as const,
    };
  }

  return data.find((media) => media.id === mediaId);
};
